import { inject } from '@angular/core';
import { RedirectFunction, Routes } from '@angular/router';

import { authGuard, guestGuard, roleGuard } from '@core/guards';
import type { Role } from '@core/models/role.model';
import { AuthService } from '@core/services/auth.service';

// Back-office roles — everything except `customer`. Customers are confined to the portal.
const ADMIN_ROLES: Role[] = ['admin', 'accountant', 'collaborator'];
const admin = { canActivate: [roleGuard], data: { roles: ADMIN_ROLES } };
// Informes hub: reports are core accountant work; collaborator uses contextual triggers only (D-D).
const reportRoles = {
  canActivate: [roleGuard],
  data: { roles: ['admin', 'accountant'] as Role[] },
};

// Role-aware landing for `/dashboard`: customers go to their portal, back-office to `home`
// (which is admin-gated). Avoids customers hitting `/forbidden` on the bare/wildcard route.
const dashboardHome: RedirectFunction = () =>
  inject(AuthService).role() === 'customer' ? 'portal' : 'home';

export const routes: Routes = [
  {
    path: 'login',
    loadChildren: () => import('@features/auth/auth.routes').then((m) => m.authRoutes),
  },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('@pages/forgot-password-page/forgot-password-page.component').then(
        (m) => m.ForgotPasswordPageComponent,
      ),
  },
  {
    path: 'forbidden',
    loadComponent: () =>
      import('@pages/forbidden-page/forbidden-page.component').then(
        (m) => m.ForbiddenPageComponent,
      ),
  },
  {
    path: 'dashboard',
    canActivate: [authGuard],
    loadComponent: () =>
      import('@pages/dashboard-page/dashboard-page.component').then(
        (m) => m.DashboardPageComponent,
      ),
    children: [
      { path: '', pathMatch: 'full', redirectTo: dashboardHome },
      {
        path: 'home',
        ...admin,
        loadChildren: () =>
          import('@pages/home-page/home-page.routes').then((m) => m.homePageRoutes),
      },
      {
        path: 'users',
        ...admin,
        loadComponent: () =>
          import('@pages/users-page/users-page.component').then((m) => m.UsersPageComponent),
      },
      {
        path: 'customers',
        ...admin,
        loadComponent: () =>
          import('@pages/customers-page/customers-page.component').then(
            (m) => m.CustomersPageComponent,
          ),
      },
      {
        path: 'customers/:customerId',
        ...admin,
        loadComponent: () =>
          import('@pages/customer-details-page/customer-details-page.component').then(
            (m) => m.CustomerDetailsPageComponent,
          ),
      },
      {
        path: 'customers/:customerId/contracts/:saleId',
        ...admin,
        loadComponent: () =>
          import('@pages/customer-contract-detail-page/customer-contract-detail-page.component').then(
            (m) => m.CustomerContractDetailPageComponent,
          ),
      },
      {
        path: 'partners',
        ...admin,
        loadComponent: () =>
          import('@pages/partners-page/partners-page.component').then(
            (m) => m.PartnersPageComponent,
          ),
      },
      {
        path: 'partners/:id',
        ...admin,
        loadComponent: () =>
          import('@pages/partner-detail-page/partner-detail-page.component').then(
            (m) => m.PartnerDetailPageComponent,
          ),
      },
      {
        path: 'advisors',
        ...admin,
        loadComponent: () =>
          import('@pages/advisors-page/advisors-page.component').then(
            (m) => m.AdvisorsPageComponent,
          ),
      },
      {
        path: 'projects',
        ...admin,
        loadComponent: () =>
          import('@pages/projects-page/projects-page.component').then(
            (m) => m.ProjectsPageComponent,
          ),
      },
      {
        path: 'projects/:id',
        ...admin,
        loadComponent: () =>
          import('@pages/project-detail-page/project-detail-page.component').then(
            (m) => m.ProjectDetailPageComponent,
          ),
      },
      {
        path: 'blueprints',
        ...admin,
        loadComponent: () =>
          import('@pages/blueprints-page/blueprints-page.component').then(
            (m) => m.BlueprintsPageComponent,
          ),
      },
      {
        path: 'blueprints/:id/edit',
        ...admin,
        loadComponent: () =>
          import('@pages/blueprint-editor-page/blueprint-editor-page.component').then(
            (m) => m.BlueprintEditorPageComponent,
          ),
      },
      {
        path: 'sales',
        ...admin,
        loadComponent: () =>
          import('@pages/sales-page/sales-page.component').then((m) => m.SalesPageComponent),
      },
      {
        path: 'cartera',
        ...admin,
        loadComponent: () =>
          import('@pages/cartera-page/cartera-page.component').then((m) => m.CarteraPageComponent),
      },
      {
        path: 'petty-cash',
        ...admin,
        loadComponent: () =>
          import('@pages/petty-cash-page/petty-cash-page.component').then(
            (m) => m.PettyCashPageComponent,
          ),
      },
      {
        path: 'informes',
        ...reportRoles,
        loadComponent: () =>
          import('@pages/informes-page/informes-page.component').then(
            (m) => m.InformesPageComponent,
          ),
      },
      {
        path: 'portal',
        canActivate: [roleGuard],
        data: { roles: ['customer'] satisfies Role[] },
        loadComponent: () =>
          import('@pages/portal-contracts-page/portal-contracts-page.component').then(
            (m) => m.PortalContractsPageComponent,
          ),
      },
      {
        path: 'portal/:saleId',
        canActivate: [roleGuard],
        data: { roles: ['customer'] satisfies Role[] },
        loadComponent: () =>
          import('@pages/portal-contract-detail-page/portal-contract-detail-page.component').then(
            (m) => m.PortalContractDetailPageComponent,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('@pages/profile-page/profile-page.component').then((m) => m.ProfilePageComponent),
      },
      {
        path: 'settings',
        ...admin,
        loadComponent: () =>
          import('@pages/settings-page/settings-page.component').then(
            (m) => m.SettingsPageComponent,
          ),
      },
      {
        path: 'help',
        ...admin,
        loadComponent: () =>
          import('@pages/help-page/help-page.component').then((m) => m.HelpPageComponent),
      },
    ],
  },
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
  { path: '**', redirectTo: 'dashboard' },
];
